/**
 * SceneClient — the bridge between the local SceneStore and the API Gateway
 * WebSocket scene protocol.
 *
 * Responsibilities:
 *   1. Open the socket, authenticate (via openSocket), subscribe to a
 *      (sessionId, mapId) and seed the store with the initial state.
 *   2. Forward local upserts (store.upsertLocal) as `scene/op` messages.
 *   3. Apply incoming `scene/op` / `scene/state` through the store's
 *      reconciler, and handle `scene/op-rejected` by rebasing against the
 *      server's current element and resending.
 *   4. Reconnect on drop with a capped backoff, and on reconnect re-subscribe
 *      so any ops we missed while disconnected come back as a fresh batch.
 *
 * Echo suppression: the server rebroadcasts a successful `scene/op` to
 * everyone in the session, including the sender. We don't suppress by
 * connection id — we rely on the store's reconciler, which is a no-op when
 * the incoming element has the same (version, versionNonce) as what we
 * already have locally. That's both simpler and more robust: even if the
 * server tags the rebroadcast with our origin, the reconciler still does the
 * right thing.
 *
 * What this file does NOT do:
 *   - Layer-permission errors aren't retried — Stage I (layer UI) will own
 *     surfacing those to the user as a toast.
 *
 * Stage H — cursors + stroke-live: inbound messages are forwarded straight
 * into the liveOverlay store (transient, TTL-expired). Outbound sends are
 * exposed as raw `sendCursor` / `sendStrokeLive` methods; throttling and
 * rate limiting live at the callsite (canvas pointer-move / pen tool) so
 * the client stays transport-agnostic.
 */

import { openSocket } from "../../lib/socket";
import type { SceneElement } from "../../lib/scene";
import { shouldApply } from "../../lib/scene";
import { SceneStore } from "../store/sceneStore";
import { liveOverlay } from "./liveOverlay";
import {
  SceneActions,
  SceneServerTypes,
  type SceneStatePayload,
  type SceneOpPayload,
  type OpRejectedPayload,
  type ErrorPayload,
  type ServerEnvelope,
  type OpRequest,
  type SubscribeRequest,
  type CursorRequest,
  type StrokeLiveRequest,
  type CursorPayload,
  type StrokeLivePayload,
} from "./sceneWire";

type ConnectionStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface SceneClientEvents {
  status?: (s: ConnectionStatus) => void;
  error?: (code: string, message: string) => void;
}

export interface SceneClientOptions {
  sessionId: string;
  mapId: string;
  /** Max backoff between reconnect attempts, in ms. */
  maxBackoffMs?: number;
}

export class SceneClient {
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = "idle";
  private backoffMs = 500;
  private readonly maxBackoffMs: number;
  /** Ops we generated locally that haven't been acknowledged by an echo yet. */
  private pending = new Map<string, SceneElement>();
  /** Set true on stop() so reconnect loops exit cleanly. */
  private stopped = false;
  /** Unhook the store subscription on stop(). */
  private unsubStore: (() => void) | null = null;
  /**
   * Snapshot of the last store state we forwarded. Lets the subscription
   * diff elements and only send real changes to the wire — subscribes fire
   * on tool switches and selection updates too, which we must ignore.
   */
  private lastElementVersion = new Map<string, number>();

  constructor(
    private readonly store: SceneStore,
    private readonly opts: SceneClientOptions,
    private readonly events: SceneClientEvents = {},
  ) {
    this.maxBackoffMs = opts.maxBackoffMs ?? 15000;
  }

  // ---------- lifecycle ----------

  async start(): Promise<void> {
    this.stopped = false;
    this.hookStore();
    await this.connectLoop();
  }

  stop(): void {
    this.stopped = true;
    this.unsubStore?.();
    this.unsubStore = null;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    // Drop any remote cursors / live strokes from the last session so the
    // canvas starts clean on the next mount.
    liveOverlay.reset();
    this.setStatus("closed");
  }

  // ---------- public send (Stage H) ----------

  /**
   * Broadcast the local user's pointer position in map coordinates.
   * No-op if the socket isn't open. Callers are expected to throttle —
   * this method does no batching.
   */
  sendCursor(x: number, y: number): void {
    const msg: CursorRequest = {
      action: SceneActions.Cursor,
      mapId: this.opts.mapId,
      x,
      y,
    };
    this.sendRaw(msg);
  }

  /**
   * Broadcast an in-progress freehand stroke batch. `points` is a flat
   * array of map-space coordinate pairs. The receiving clients render
   * these transiently; the committed stroke arrives later as a normal
   * scene/op and overwrites the transient preview.
   */
  sendStrokeLive(
    elementId: string,
    points: number[],
    pressures: number[] | undefined,
    strokeColor: string | undefined,
    strokeWidth: number | undefined,
  ): void {
    if (points.length < 4) return; // need at least two points for a line
    const msg: StrokeLiveRequest = {
      action: SceneActions.StrokeLive,
      mapId: this.opts.mapId,
      elementId,
      points,
      pressures,
      strokeColor,
      strokeWidth,
    };
    this.sendRaw(msg);
  }

  // ---------- internals ----------

  private setStatus(s: ConnectionStatus): void {
    if (this.status === s) return;
    this.status = s;
    this.events.status?.(s);
  }

  private async connectLoop(): Promise<void> {
    while (!this.stopped) {
      try {
        this.setStatus(this.backoffMs === 500 ? "connecting" : "reconnecting");
        const ws = await openSocket();
        this.ws = ws;

        await this.waitForOpen(ws);
        this.setStatus("open");
        this.backoffMs = 500; // reset on successful connect

        this.attachHandlers(ws);
        this.sendSubscribe();

        await this.waitForClose(ws);
      } catch (err) {
        // Swallow — the loop below will back off and retry.
        console.warn("[SceneClient] connect failed", err);
      }

      this.ws = null;
      if (this.stopped) break;

      // Exponential backoff with jitter so a mass-disconnect doesn't thunder
      // back at the API at the same moment.
      const jitter = Math.random() * 200;
      await sleep(this.backoffMs + jitter);
      this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
    }
  }

  private waitForOpen(ws: WebSocket): Promise<void> {
    if (ws.readyState === WebSocket.OPEN) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onOpen = () => {
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("error", onError);
        resolve();
      };
      const onError = (ev: Event) => {
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("error", onError);
        reject(new Error(`WebSocket error before open: ${ev.type}`));
      };
      ws.addEventListener("open", onOpen);
      ws.addEventListener("error", onError);
    });
  }

  private waitForClose(ws: WebSocket): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        ws.removeEventListener("close", done);
        ws.removeEventListener("error", done);
        resolve();
      };
      ws.addEventListener("close", done);
      ws.addEventListener("error", done);
    });
  }

  private attachHandlers(ws: WebSocket): void {
    ws.addEventListener("message", (ev) => this.onMessage(ev.data));
  }

  // ---------- outbound ----------

  private sendSubscribe(): void {
    const msg: SubscribeRequest = {
      action: SceneActions.Subscribe,
      sessionId: this.opts.sessionId,
      mapId: this.opts.mapId,
    };
    this.sendRaw(msg);
    // Any locally-authored elements that predate this connection (or were
    // written during a disconnect) need to be resent — the server fanout
    // missed them while we were offline.
    for (const el of this.pending.values()) {
      this.sendOp(el);
    }
  }

  private sendOp(element: SceneElement): void {
    const msg: OpRequest = {
      action: SceneActions.Op,
      mapId: this.opts.mapId,
      element,
    };
    this.pending.set(element.id, element);
    this.sendRaw(msg);
  }

  private sendRaw(obj: unknown): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // Silently drop — the reconnect loop will resubscribe and the
      // pending map keeps our unacknowledged work alive.
      return;
    }
    try {
      ws.send(JSON.stringify(obj));
    } catch (err) {
      console.warn("[SceneClient] send failed", err);
    }
  }

  // ---------- store subscription ----------

  private hookStore(): void {
    // Seed the version map so the first subscribe-callback sees everything
    // we already have (unlikely on first mount, but matters after reconnect
    // when the store may already hold offline edits).
    for (const el of this.store.listElements()) {
      this.lastElementVersion.set(el.id, el.version);
    }

    this.unsubStore = this.store.subscribe(() => {
      // Drain only the elements the store says were authored locally
      // since the last notify. This is the authoritative signal for
      // "should broadcast" — testing ownerId would skip cases where a
      // DM moves a player-owned element, and testing raw version diffs
      // would double-send incoming remote applies.
      const dirty = this.store.consumeLocalDirty();
      if (dirty.length === 0) return;
      const state = this.store.getState();
      for (const id of dirty) {
        const el = state.elements[id];
        if (!el) continue;
        this.lastElementVersion.set(el.id, el.version);
        this.sendOp(el);
      }
    });
  }

  // ---------- inbound ----------

  private onMessage(raw: unknown): void {
    if (typeof raw !== "string") return;
    let env: ServerEnvelope;
    try {
      env = JSON.parse(raw);
    } catch {
      console.warn("[SceneClient] dropped unparseable message");
      return;
    }

    switch (env.type) {
      case SceneServerTypes.SceneState:
        this.onSceneState(env.payload as SceneStatePayload);
        break;
      case SceneServerTypes.SceneOp:
        this.onSceneOp(env.payload as SceneOpPayload);
        break;
      case SceneServerTypes.OpRejected:
        this.onOpRejected(env.payload as OpRejectedPayload);
        break;
      case SceneServerTypes.Cursor:
        this.onCursor(env.payload as CursorPayload);
        break;
      case SceneServerTypes.StrokeLive:
        this.onStrokeLive(env.payload as StrokeLivePayload);
        break;
      case SceneServerTypes.Error: {
        const p = env.payload as ErrorPayload;
        console.warn(`[SceneClient] server error ${p.code}: ${p.message}`);
        this.events.error?.(p.code, p.message);
        break;
      }
      case SceneServerTypes.Ack:
        break;
      default:
        // Unknown type — ignore, keeps us forward-compatible.
        break;
    }
  }

  private onSceneState(p: SceneStatePayload): void {
    if (p.mapId !== this.opts.mapId) return;
    // Any locally-pending ops we haven't heard back about are still ours;
    // the reconciler will sort out conflicts when they land. We rebuild the
    // version snapshot from whatever the server sent, then merge pending
    // back in on top so a resubscribe doesn't roll back local drawing.
    this.store.applyRemoteBatch(p.elements);
    this.syncVersionMap();
    // Re-send anything still pending — the server may have missed it.
    for (const el of this.pending.values()) {
      // Only resend if the server's version is strictly older; otherwise
      // the server already has our edit (or a newer one) and we're done.
      const current = this.store.getElement(el.id);
      if (!current || shouldApply(current, el)) {
        this.sendOp(el);
      } else {
        this.pending.delete(el.id);
      }
    }
  }

  private onSceneOp(p: SceneOpPayload): void {
    if (p.mapId !== this.opts.mapId) return;
    const applied = this.store.applyRemote(p.element);
    if (applied) {
      this.lastElementVersion.set(p.element.id, p.element.version);
    }
    // The committed version of a stroke has the same id as its live
    // preview — drop the transient entry so the overlay doesn't keep
    // ghost-rendering an extra copy on top of the persisted element.
    liveOverlay.clearStroke(p.element.id);
    // If this echo carries our own version, we can clear it from pending.
    const pending = this.pending.get(p.element.id);
    if (pending && pending.version === p.element.version && pending.versionNonce === p.element.versionNonce) {
      this.pending.delete(p.element.id);
    }
  }

  private onOpRejected(p: OpRejectedPayload): void {
    // Version conflict: the server has a newer element and handed us back
    // its current state. Fold that into the store, then rebase our local
    // intent on top by stamping a fresh version and resending.
    if (!p.current) {
      // Non-version rejection (e.g. layer permission). Drop the pending op;
      // there's nothing a rebase can do. Stage I will wire up a toast.
      this.pending.delete(p.elementId);
      this.events.error?.("op-rejected", `${p.reason} (${p.elementId})`);
      return;
    }

    // Always accept the server's current state first so the local view
    // shows the truth before we retry.
    this.store.applyRemote(p.current);
    this.lastElementVersion.set(p.current.id, p.current.version);

    const pending = this.pending.get(p.elementId);
    if (!pending) return;

    // Rebase: keep the user's intent (geometry, style, points, etc.) but
    // stamp a version that's strictly newer than the server's. bumpLocal
    // produces version+1 on top of whatever we pass in, so feed it the
    // server's current version to guarantee we win the next round.
    const rebased = this.store.bumpLocal({ ...pending, version: p.current.version, versionNonce: p.current.versionNonce });
    // Re-upsert locally (fresh undo entry? no — this is a retry, not a new
    // user action). We can't cleanly suppress undo for this one without
    // contorting the store API; Stage E accepts the duplicate undo entry
    // in exchange for a simpler store. Tweak in Stage D+ if it's annoying.
    this.store.upsertLocal(rebased);
    // upsertLocal will trigger our subscription which resends via sendOp.
  }

  private onCursor(p: CursorPayload): void {
    if (p.mapId !== this.opts.mapId) return;
    // Filter own echoes just in case a second tab shares our sub.
    if (p.userId === this.store.getState().userId) return;
    liveOverlay.setCursor({
      userId: p.userId,
      username: p.username,
      x: p.x,
      y: p.y,
      updatedAt: Date.now(),
    });
  }

  private onStrokeLive(p: StrokeLivePayload): void {
    if (p.mapId !== this.opts.mapId) return;
    if (p.userId === this.store.getState().userId) return;
    if (!p.points || p.points.length < 4) return;
    liveOverlay.setStroke({
      elementId: p.elementId,
      userId: p.userId,
      points: p.points,
      pressures: p.pressures,
      strokeColor: p.strokeColor,
      strokeWidth: p.strokeWidth,
      updatedAt: Date.now(),
    });
  }

  private syncVersionMap(): void {
    this.lastElementVersion.clear();
    for (const [id, el] of Object.entries(this.store.getState().elements)) {
      this.lastElementVersion.set(id, el.version);
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
